import { AbsoluteFill, interpolate, useCurrentFrame } from "remotion";
import {
  Caption,
  Callout,
  CodePanel,
  COLORS,
  FONTS,
  Panel,
  PipelineGraph,
  SceneTransition,
  SlideTimeBar,
  type PipeNode,
} from "../../coldcompute";
import { SCENE } from "./constants";
import {
  HOOK,
  PROBLEM,
  DETECTOR,
  VALIDATE,
  WHERE,
  REDACT,
  HONEST,
  OUTRO,
} from "./script";

const center: React.CSSProperties = {
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
};

const Kicker: React.FC<{ text: string; color: string }> = ({ text, color }) => (
  <div
    style={{
      fontFamily: FONTS.mono,
      fontSize: 22,
      letterSpacing: "0.22em",
      textTransform: "uppercase",
      color,
      marginBottom: 22,
    }}
  >
    ▍{text}
  </div>
);

const Big: React.FC<{ children: React.ReactNode; size?: number }> = ({
  children,
  size = 92,
}) => (
  <div
    style={{
      fontFamily: FONTS.display,
      fontWeight: 700,
      fontSize: size,
      lineHeight: 0.98,
      letterSpacing: "-0.02em",
      color: COLORS.text,
      textAlign: "center",
      maxWidth: 1560,
    }}
  >
    {children}
  </div>
);

/* 01 — HOOK: a real secret would land in the prompt */
export const Scene01Hook: React.FC = () => {
  const frame = useCurrentFrame();
  const flash = interpolate(frame, [40, 70], [0.3, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.hook.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="rag · what almost leaked" color={COLORS.red} />
          <Big>
            A source chunk had a real{" "}
            <span style={{ color: COLORS.red, opacity: flash }}>SSN</span>
            <br />— about to land in the{" "}
            <span style={{ color: COLORS.cyan }}>model's prompt</span>
          </Big>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={HOOK} accent="red" />
    </AbsoluteFill>
  );
};

/* 02 — PROBLEM: RAG feeds whatever it retrieves */
export const Scene02Problem: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.problem.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Kicker text="the exposure" color={COLORS.red} />
        <Panel accent="red" title="retrieved chunk · raw" width={980}>
          <div
            style={{
              fontFamily: FONTS.mono,
              fontSize: 24,
              lineHeight: 1.9,
              color: COLORS.text,
            }}
          >
            <div>contact the lead at{" "}
              <span style={{ color: COLORS.red }}>jane.doe@acme.com</span>
            </div>
            <div>account ssn{" "}
              <span style={{ color: COLORS.red }}>123-45-6789</span>,
              ip <span style={{ color: COLORS.red }}>10.0.4.12</span>
            </div>
            <div style={{ color: COLORS.dim, marginTop: 8 }}>
              ↳ fed verbatim into the LLM prompt
            </div>
          </div>
        </Panel>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={PROBLEM} accent="red" />
  </AbsoluteFill>
);

/* 03 — DETECTOR: the 7 default types */
export const Scene03Detector: React.FC = () => {
  const frame = useCurrentFrame();
  const types = [
    { t: "email", c: COLORS.cyan },
    { t: "phone", c: COLORS.cyan },
    { t: "ssn", c: COLORS.green },
    { t: "credit_card", c: COLORS.green },
    { t: "ip_address", c: COLORS.lime },
    { t: "api_key", c: COLORS.lime },
    { t: "aws_key", c: COLORS.amber },
  ];
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.detector.duration} />
      <AbsoluteFill style={center}>
        <Kicker text="detectPII · 7 default types" color={COLORS.green} />
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 16,
            justifyContent: "center",
            maxWidth: 1500,
          }}
        >
          {types.map((x, i) => {
            const appear = interpolate(
              frame,
              [10 + i * 8, 26 + i * 8],
              [0, 1],
              { extrapolateLeft: "clamp", extrapolateRight: "clamp" },
            );
            return (
              <div
                key={x.t}
                style={{
                  opacity: appear,
                  transform: `translateY(${(1 - appear) * 12}px)`,
                  fontFamily: FONTS.mono,
                  fontSize: 30,
                  color: x.c,
                  background: COLORS.panel,
                  border: `1.5px solid ${x.c}66`,
                  borderRadius: 10,
                  padding: "14px 22px",
                }}
              >
                {x.t}
              </div>
            );
          })}
        </div>
        <div
          style={{
            fontFamily: FONTS.body,
            fontSize: 26,
            color: COLORS.dim,
            marginTop: 30,
          }}
        >
          regex-based · no LLM call · &lt;1ms per chunk ·{" "}
          <span style={{ color: COLORS.dim }}>date_of_birth = off by default</span>
        </div>
      </AbsoluteFill>
      <Caption lines={DETECTOR} accent="green" />
    </AbsoluteFill>
  );
};

/* 04 — VALIDATE: regex + a second check to kill false positives */
const VALIDATE_CODE = `// pii-detector.ts — post-validation after the regex match
if (type === 'credit_card' && !luhnCheck(value)) continue
if (type === 'ssn'         && !isLikelySSN(value)) continue
if (type === 'phone' && value.replace(/\\D/g,'').length < 10) continue

function isLikelySSN(value) {
  const d = value.replace(/\\D/g, '')
  const area = parseInt(d.slice(0, 3), 10)
  // 000, 666, 900-999 are not valid SSN area numbers
  if (area === 0 || area === 666 || area >= 900) return false
  return true
}`;
export const Scene04Validate: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.validate.duration} />
    <AbsoluteFill
      style={{ alignItems: "center", justifyContent: "center", paddingBottom: 40 }}
    >
      <CodePanel
        code={VALIDATE_CODE}
        language="javascript"
        filename="pii-detector.ts — validation"
        highlightLines={[2, 3, 9]}
        revealFrames={240}
        fontSize={24}
        width={1280}
      />
    </AbsoluteFill>
    <Caption lines={VALIDATE} accent="green" />
  </AbsoluteFill>
);

/* 05 — WHERE: context (before LLM) + output (before user) */
const PIPE: PipeNode[] = [
  { label: "retrieve", value: "CHUNKS", sub: "raw PII", accent: "red" },
  { label: "context guard", value: "REDACT", sub: "before LLM", accent: "green" },
  { label: "llm", value: "ANSWER", sub: "no secret", accent: "cyan" },
  { label: "output guard", value: "REDACT", sub: "before you", accent: "lime" },
];
export const Scene05Where: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.where.duration} />
    <AbsoluteFill style={{ ...center, padding: "0 80px" }}>
      <Kicker text="chat route · two redaction points" color={COLORS.cyan} />
      <div style={{ width: 1700 }}>
        <PipelineGraph nodes={PIPE} startFrame={6} stepFrames={24} nodeWidth={230} />
      </div>
      <div
        style={{
          fontFamily: FONTS.mono,
          fontSize: 22,
          color: COLORS.dim,
          marginTop: 34,
        }}
      >
        applyContextGuardrails(chunks) → applyOutputGuardrails(answer)
      </div>
    </AbsoluteFill>
    <Caption lines={WHERE} accent="cyan" />
  </AbsoluteFill>
);

/* 06 — REDACT: mask vs partial */
export const Scene06Redact: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.redact.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Kicker text="how it scrubs" color={COLORS.lime} />
        <div style={{ display: "flex", gap: 28, alignItems: "stretch" }}>
          <Panel accent="green" title="strategy · mask (default)" width={520}>
            <div
              style={{
                fontFamily: FONTS.mono,
                fontSize: 24,
                lineHeight: 2,
                color: COLORS.text,
              }}
            >
              <div>jane.doe@acme.com</div>
              <div style={{ color: COLORS.dim }}>↓</div>
              <div style={{ color: COLORS.green }}>[REDACTED]</div>
            </div>
          </Panel>
          <Panel accent="lime" title="strategy · partial" width={520}>
            <div
              style={{
                fontFamily: FONTS.mono,
                fontSize: 24,
                lineHeight: 2,
                color: COLORS.text,
              }}
            >
              <div>123-45-6789</div>
              <div style={{ color: COLORS.dim }}>↓</div>
              <div style={{ color: COLORS.lime }}>***-**-6789</div>
            </div>
          </Panel>
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={REDACT} accent="lime" />
  </AbsoluteFill>
);

/* 07 — HONEST: regex limits + fail-open */
export const Scene07Honest: React.FC = () => {
  const frame = useCurrentFrame();
  const c1 = interpolate(frame, [20, 50], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const c2 = interpolate(frame, [60, 90], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.honest.duration} />
      <AbsoluteFill style={center}>
        <Kicker text="honest limits · not magic" color={COLORS.amber} />
        <div style={{ display: "flex", gap: 30 }}>
          <div style={{ opacity: c1 }}>
            <Callout label="regex, not AI" accent="amber" width={420}>
              Catches <b style={{ color: COLORS.text }}>known formats</b>.
              A novel layout it can't pattern-match slips through.
            </Callout>
          </div>
          <div style={{ opacity: c2 }}>
            <Callout label="fail-open by design" accent="amber" width={420}>
              Wrapped in <b style={{ color: COLORS.text }}>try/catch</b> —
              on error it logs a warning and lets the request continue.
            </Callout>
          </div>
        </div>
      </AbsoluteFill>
      <Caption lines={HONEST} accent="amber" />
    </AbsoluteFill>
  );
};

/* 08 — OUTRO */
export const Scene08Outro: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.outro.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Big size={70}>
          Known PII never reaches
          <br />
          the model — or the answer.
          <br />
          <span style={{ color: COLORS.green }}>A fast first line of defence.</span>
        </Big>
        <div
          style={{
            fontFamily: FONTS.mono,
            fontSize: 24,
            letterSpacing: "0.24em",
            color: COLORS.dim,
            marginTop: 44,
          }}
        >
          EAKC · guardrails · red-team eval
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={OUTRO} accent="green" />
  </AbsoluteFill>
);

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
